/**
 * Scope helpers for the deal Map tab: which of the viewer's tracts belong to
 * the deal and which are neighbours, which counties to load, and camera
 * bounds. Pure. The rows are server-scoped (tenancy on /deals, /lease_deeds
 * and /lease_deed_tracts), so everything here is already authorised.
 */
import { canonicalTrs } from "./aliquot";
import { normalizeCounty } from "./trsAggregation";
import { statusBucket } from "./tractStatusBucket";

/**
 * Distinct (state, county) pairs that hold tracts, upper-cased state and
 * `normalizeCounty`'d county ("Grady County" -> "GRADY"), sorted. These are
 * the counties whose sections / OCC units the map loads.
 *
 * @returns {Array<{ state: string, county: string }>}
 */
export const tractCounties = (rows) => {
  const seen = new Map();
  for (const r of Array.isArray(rows) ? rows : []) {
    const state = String(r?.state ?? "").trim().toUpperCase();
    const county = normalizeCounty(r?.county);
    if (!state || !county) continue;
    seen.set(`${state}|${county}`, { state, county });
  }
  return [...seen.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, v]) => v);
};

/**
 * Bbox [minX, minY, maxX, maxY] over Polygon / MultiPolygon features, or null.
 */
export const bboxOfFeatures = (features) => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const visit = (ring) => {
    for (const [x, y] of ring) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  };
  for (const f of Array.isArray(features) ? features : []) {
    const g = f?.geometry;
    if (g?.type === "Polygon") g.coordinates.forEach(visit);
    else if (g?.type === "MultiPolygon") g.coordinates.forEach((p) => p.forEach(visit));
  }
  return Number.isFinite(minX) ? [minX, minY, maxX, maxY] : null;
};

/**
 * The viewer's tract rows for a SET of deals (the deals list's map).
 *
 * @param {Array<object>} rows  joined tract rows (dealTracts.joinTractRows)
 * @param {Iterable<number|string>|null} dealIds  null = every row
 * @param {{ includeDead?: boolean }} [options]  dead deals are dropped unless set
 * @returns {object[]}
 */
export const dealSetTracts = (rows, dealIds, options = {}) => {
  // Compared as strings: grid ids and tract deal_ids need not share a type.
  const want = dealIds ? new Set([...dealIds].map(String)) : null;
  return (Array.isArray(rows) ? rows : []).filter(
    (r) =>
      (!want || want.has(String(r?.deal_id))) &&
      (options.includeDead || statusBucket(r?.deal_status) !== "dead")
  );
};

/**
 * Split the viewer's tract rows for one deal's map.
 *
 *   own         this deal's tracts (deal_id === dealId)
 *   neighbours  OTHER deals' tracts in any section this deal has a tract in
 *               (same canonical TRS, any aliquot) — the competing offers on
 *               the same quarter calls. Rows are already server-scoped, so a
 *               neighbour is only ever a deal the viewer may see.
 *   sections    the canonical TRS keys of this deal's tracts
 *
 * Rows without a TRS can be this deal's (they show as unmappable) but are
 * never neighbours.
 *
 * @param {Array<object>} rows  joined tract rows (dealTracts.joinTractRows)
 * @param {number|string} dealId
 * @returns {{ own: object[], neighbours: object[], sections: Set<string> }}
 */
export const splitDealTracts = (rows, dealId) => {
  const list = Array.isArray(rows) ? rows : [];
  const want = dealId === null || dealId === undefined ? null : String(dealId);
  const own = list.filter((r) => want !== null && String(r?.deal_id) === want);
  const sections = new Set();
  for (const r of own) {
    const key = canonicalTrs(r?.township, r?.range, r?.section);
    if (key) sections.add(key);
  }
  const neighbours = list.filter((r) => {
    if (want === null || String(r?.deal_id) === want) return false;
    const key = canonicalTrs(r?.township, r?.range, r?.section);
    return !!key && sections.has(key);
  });
  return { own, neighbours, sections };
};
